import { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Check, X, SkipForward, RotateCw, ZoomIn } from 'lucide-react';

/**
 * Crops the source image using canvas based on the crop pixel area + rotation.
 * Returns a high-quality JPEG Blob.
 */
const getCroppedImageBlob = (imageSrc, cropAreaPixels, rotation = 0, quality = 0.95) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      // For rotation: compute bounding box
      const rotRad = (rotation * Math.PI) / 180;
      const sin = Math.abs(Math.sin(rotRad));
      const cos = Math.abs(Math.cos(rotRad));
      const bBoxW = image.width * cos + image.height * sin;
      const bBoxH = image.width * sin + image.height * cos;

      // Draw rotated image onto a temp canvas
      const temp = document.createElement('canvas');
      const tctx = temp.getContext('2d');
      temp.width = bBoxW;
      temp.height = bBoxH;
      tctx.translate(bBoxW / 2, bBoxH / 2);
      tctx.rotate(rotRad);
      tctx.drawImage(image, -image.width / 2, -image.height / 2);

      // Now extract the cropped area from the rotated canvas
      canvas.width = cropAreaPixels.width;
      canvas.height = cropAreaPixels.height;
      ctx.drawImage(
        temp,
        cropAreaPixels.x,
        cropAreaPixels.y,
        cropAreaPixels.width,
        cropAreaPixels.height,
        0,
        0,
        cropAreaPixels.width,
        cropAreaPixels.height,
      );

      canvas.toBlob(
        (blob) => {
          if (blob) resolve(blob);
          else reject(new Error('Canvas is empty'));
        },
        'image/jpeg',
        quality,
      );
    };
    image.onerror = (err) => reject(err);
    image.src = imageSrc;
  });

const ImageCropModal = ({ open, imageSrc, fileName, aspect = 1, recommendedDimensions, onCropComplete, onSkip, onCancel }) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [processing, setProcessing] = useState(false);

  const handleCropComplete = useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const handleSave = async () => {
    if (!croppedAreaPixels || !imageSrc) return;
    setProcessing(true);
    try {
      const blob = await getCroppedImageBlob(imageSrc, croppedAreaPixels, rotation, 0.95);
      // Convert blob to File so backend gets a proper filename
      const safeName = (fileName || 'cropped-image.jpg').replace(/\.[^.]+$/, '.jpg');
      const file = new File([blob], safeName, { type: 'image/jpeg' });
      onCropComplete(file);
    } catch (err) {
      console.error('Crop failed:', err);
    } finally {
      setProcessing(false);
    }
  };

  const handleReset = () => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent className="max-w-2xl p-0 overflow-hidden" data-testid="image-crop-modal">
        <DialogHeader className="px-6 pt-6 pb-2">
          <DialogTitle>Crop & Adjust Image</DialogTitle>
        </DialogHeader>

        {/* Crop area */}
        <div className="relative w-full h-[60vh] max-h-[440px] bg-slate-900">
          {imageSrc && (
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              rotation={rotation}
              aspect={aspect}
              minZoom={1}
              maxZoom={4}
              restrictPosition={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onRotationChange={setRotation}
              onCropComplete={handleCropComplete}
              objectFit="contain"
            />
          )}
        </div>

        {/* Controls */}
        <div className="px-6 py-4 space-y-3 border-t border-slate-200 bg-white">
          <div className="flex items-center gap-3">
            <ZoomIn className="w-4 h-4 text-slate-500 shrink-0" />
            <Slider
              min={1}
              max={4}
              step={0.05}
              value={[zoom]}
              onValueChange={(v) => setZoom(v[0])}
              className="flex-1"
              data-testid="crop-zoom-slider"
            />
            <span className="text-xs font-mono text-slate-600 w-10 text-right">{zoom.toFixed(2)}x</span>
          </div>
          <div className="flex items-center gap-3">
            <RotateCw className="w-4 h-4 text-slate-500 shrink-0" />
            <Slider
              min={0}
              max={360}
              step={1}
              value={[rotation]}
              onValueChange={(v) => setRotation(v[0])}
              className="flex-1"
              data-testid="crop-rotation-slider"
            />
            <span className="text-xs font-mono text-slate-600 w-10 text-right">{rotation}°</span>
          </div>
          <p className="text-xs text-slate-500 text-center">
            Drag to reposition • Pinch / scroll to zoom • Saves as high-quality JPEG
            {recommendedDimensions && (
              <span className="block mt-1 text-slate-400">Recommended: {recommendedDimensions}</span>
            )}
          </p>
        </div>

        {/* Action buttons */}
        <div className="px-6 pb-6 pt-2 grid grid-cols-1 sm:grid-cols-3 gap-2 bg-white">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={processing}
            className="border-slate-300"
            data-testid="crop-cancel-btn"
          >
            <X className="w-4 h-4 mr-2" />
            Cancel
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={onSkip}
            disabled={processing}
            className="border-slate-300"
            data-testid="crop-skip-btn"
          >
            <SkipForward className="w-4 h-4 mr-2" />
            Skip Cropping
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={processing || !croppedAreaPixels}
            className="bg-[#0047AB] hover:bg-[#003380] text-white"
            data-testid="crop-save-btn"
          >
            <Check className="w-4 h-4 mr-2" />
            {processing ? 'Saving...' : 'Crop & Save'}
          </Button>
        </div>

        {/* Reset (small footer link) */}
        <div className="px-6 pb-3 -mt-2 text-center bg-white">
          <button
            type="button"
            onClick={handleReset}
            disabled={processing}
            className="text-xs text-slate-500 hover:text-slate-700 underline"
            data-testid="crop-reset-btn"
          >
            Reset adjustments
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ImageCropModal;
